import { getAddedParticipantNames, getRemovedParticipantNames, isEnrollmentEventRenewal } from 'components/member_onboarding_v2/utils'
import { listToProseString } from 'components/members/utils'
import { type MemberOnboardingEnrollmentEvent, type MemberOnboardingMemberPlanChangesProps, type MemberOnboardingRestrictions } from 'utils/web_api.schemas'

import { DentalPlanChangesTable } from '../PlanChangesTable/DentalPlanChangesTable'
import { MedicalPlanChangesTable } from '../PlanChangesTable/MedicalPlanChangesTable'
import { VisionPlanChangesTable } from '../PlanChangesTable/VisionPlanChangesTable'

export interface PlanDetailsSectionProps {
  planChangeSummary: MemberOnboardingMemberPlanChangesProps;
  hasFamily: boolean;
  medicalPlanNoPlanMessage?: string;
  restrictions?: MemberOnboardingRestrictions;
  enrollmentEvent?: MemberOnboardingEnrollmentEvent | null;
}

/**
 * Returns the description text for the QLE plan details view based on
 * whether dependents are being added or removed.
 */
function getQLEPlanDetailsDescription (planChangeSummary: MemberOnboardingMemberPlanChangesProps): string {
  const currentParticipants = planChangeSummary.current_plan_year.covered_participants || []
  const nextParticipants = planChangeSummary.next_plan_year.covered_participants || []

  const addedNames = getAddedParticipantNames(currentParticipants, nextParticipants)
  const removedNames = getRemovedParticipantNames(currentParticipants, nextParticipants)

  const hasDependentsChanged = addedNames.length > 0 || removedNames.length > 0

  if (!hasDependentsChanged) {
    return "As a reminder, you're currently covered by:"
  }

  // Determine if adding or removing
  const isAdding = addedNames.length > 0
  const names = isAdding ? addedNames : removedNames
  const isPlural = names.length > 1
  const namesList = listToProseString(names)

  if (isAdding) {
    return `If you're just adding your dependent${isPlural ? 's' : ''} ${namesList} to your existing coverage, it'd look like:`
  } else {
    return `If you're just removing your dependent${isPlural ? 's' : ''} ${namesList} from your existing coverage, it'd look like:`
  }
}

export const PlanDetailsSection = ({
  planChangeSummary,
  hasFamily,
  restrictions = {},
  medicalPlanNoPlanMessage,
  enrollmentEvent,
}: PlanDetailsSectionProps) => {
  const isRenewals = isEnrollmentEventRenewal(enrollmentEvent)
  const title = isRenewals ? `Plan changes for ${enrollmentEvent?.plan_year}` : 'Your current plans'
  const description = isRenewals
    ? `Your insurance coverage can vary from year to year, which may also
          impact your overall healthcare costs.`
    : getQLEPlanDetailsDescription(planChangeSummary)

  return (
    <div data-testid='plan-details-section'>
      <div className='mb-10'>
        <h1 className='heading-lg text-gray-800 mb-3'>
          {title}
        </h1>
        <p className='body text-gray-500'>
          {description}
        </p>
      </div>
      <div className='flex flex-col gap-8'>
        <MedicalPlanChangesTable
          planChangeSummary={planChangeSummary}
          hasFamily={hasFamily}
          thereIsNoNextPlanMessage={medicalPlanNoPlanMessage}
          isRenewals={isRenewals}
        />
        <DentalPlanChangesTable
          planChangeSummary={planChangeSummary}
          hasFamily={hasFamily}
          restrictions={restrictions}
          isRenewals={isRenewals}
        />
        <VisionPlanChangesTable
          planChangeSummary={planChangeSummary}
          restrictions={restrictions}
          isRenewals={isRenewals}
        />
      </div>
    </div>
  )
}
